import type { components } from "../api/schema";
import { apiClient } from "./apiClient";
import { ApiRequestError } from "./errors";
import { MAX_PAGE_SIZE } from "./pagination";
import { unicodeCaseFold } from "./unicodeCaseFold";

export type VariableResponse = components["schemas"]["VariableResponse"];
export type DeclarationConflict = "none" | "value_type" | "allowed_values";

export async function fetchAllVariablesForAgent(
  agentId: string,
  signal?: AbortSignal,
): Promise<VariableResponse[]> {
  const collected: VariableResponse[] = [];
  let pageToken: string | undefined;
  do {
    signal?.throwIfAborted();
    const { data, error } = await apiClient.GET("/v1/variables", {
      ...(signal === undefined ? {} : { signal }),
      params: {
        query: {
          agent_id: agentId,
          page_size: MAX_PAGE_SIZE,
          page_token: pageToken,
        },
      },
    });
    if (error) throw new ApiRequestError(error);
    collected.push(...data.items);
    pageToken = data.next_page_token ?? undefined;
  } while (pageToken);
  return collected;
}

/** Revision identity, including renames, without copying values into a query key. */
export function variableDeclarationsFingerprint(
  variables: readonly VariableResponse[],
): string {
  return JSON.stringify(
    variables
      .map(({ id, key, current_revision }) => [id, key, current_revision.id])
      .sort(([left = ""], [right = ""]) => left.localeCompare(right)),
  );
}

// Persona values bind to a declaration by key name (docs/01-product-model.md
// (The variable system (the cascade))): an undefaulted `scenario`-scope slot is
// a persona contract, and a persona also supplies its own `persona`-scope
// declarations. One rule, read both by the persona editor (which keys are
// offered) and by the rename warning (which renames can detach values), so the
// two surfaces can never disagree about what a persona can supply.
export interface PersonaSuppliableDeclaration {
  hasDefault: boolean;
  scopeId: string;
  scopeType: string;
}

export function isPersonaSuppliableDeclaration(
  declaration: PersonaSuppliableDeclaration,
  personaId?: string,
): boolean {
  if (declaration.scopeType === "scenario") {
    return !declaration.hasDefault;
  }
  return (
    personaId !== undefined &&
    declaration.scopeType === "persona" &&
    declaration.scopeId === personaId
  );
}

export function groupDeclarationsByKey(
  variables: VariableResponse[],
): Map<string, VariableResponse[]> {
  const groups = new Map<string, VariableResponse[]>();
  for (const variable of variables) {
    const normalizedKey = unicodeCaseFold(variable.key);
    const group = groups.get(normalizedKey);
    if (group) {
      group.push(variable);
    } else {
      groups.set(normalizedKey, [variable]);
    }
  }
  return groups;
}

export function cascadeFlags(group: VariableResponse[]): {
  isRequired: boolean;
  isSensitive: boolean;
} {
  return {
    // ASSUMED(is_required unions like is_sensitive; it gates launches, not data)
    isRequired: group.some((variable) => variable.current_revision.is_required),
    isSensitive: group.some(
      (variable) => variable.current_revision.is_sensitive,
    ),
  };
}

export function declarationConflict(
  group: VariableResponse[],
): DeclarationConflict {
  // value_type and allowed_values precedence across declarations is unsettled;
  // report disagreement without unioning, picking a winner, or reordering.
  const first = group[0];
  if (!first) return "none";
  if (
    group.some(
      (variable) =>
        variable.current_revision.value_type !==
        first.current_revision.value_type,
    )
  ) {
    return "value_type";
  }
  const firstAllowed = JSON.stringify(first.current_revision.allowed_values);
  if (
    group.some(
      (variable) =>
        JSON.stringify(variable.current_revision.allowed_values) !==
        firstAllowed,
    )
  ) {
    return "allowed_values";
  }
  return "none";
}

export function declarationConflictMessage(
  key: string,
  count: number,
  conflict: Exclude<DeclarationConflict, "none">,
): string {
  const disagreement =
    conflict === "value_type" ? "different types" : "different allowed values";
  return `${key} is declared on ${String(count)} scopes with ${disagreement}; make them match before overriding it.`;
}

export function contentionMessages(group: VariableResponse[]): string[] {
  const first = group[0];
  if (!first || group.length < 2) return [];
  const scopes = [...new Set(group.map((variable) => variable.scope_type))];
  const messages = [
    `“${first.key}” is declared on ${String(group.length)} scopes (${scopes.join(", ")}).`,
  ];
  if (cascadeFlags(group).isSensitive) {
    messages.push(
      "One declaration is sensitive, so this value is masked everywhere.",
    );
  }
  const conflict = declarationConflict(group);
  if (conflict !== "none") {
    messages.push(
      conflict === "value_type"
        ? "These declarations disagree on type; make them match."
        : "These declarations disagree on allowed values; make them match.",
    );
  }
  return messages;
}
